import { ShipmentMethod as CrewReturnShipmentTypeEnum } from 'corso-types';
import { isExchangeClaim } from '~/utils/claimLineItem';
import { ExchangeMethod, exchangeMethodLabel } from '~/utils/constants';
import { makeCrewClaimWithClaimLineItemsLoader } from '~/utils/routing';

type CrewContext = Parameters<
  Parameters<typeof makeCrewClaimWithClaimLineItemsLoader>[0]
>[0]['context'];

const checkInstantExchangeAvailable = (context: CrewContext) => {
  const {
    settings,
    order,
    crewClaim: { claimLineItems, returnMethod },
  } = context;

  const stripeConfigured = Boolean(settings.stripeConfig);
  const instantExchangeOffered = Boolean(order?.offerInstantExchange);
  const allExchangeClaims = claimLineItems.every(isExchangeClaim);
  const hasReturnLabel =
    returnMethod?.type === CrewReturnShipmentTypeEnum.label;

  const pretaxExchangeDeltaAmount = claimLineItems
    .filter(isExchangeClaim)
    .map((cli) => {
      if (cli.advancedExchangeMethod === 'NO_PAYMENT_OR_REFUND') return 0;
      const claimLineItemTotal = cli.unitPrice * cli.quantity;
      const variantTotal = cli.variant.price * cli.quantity;
      return variantTotal - claimLineItemTotal;
    })
    .reduce((acc, curr) => acc + curr, 0);

  return (
    pretaxExchangeDeltaAmount >= 0 &&
    stripeConfigured &&
    instantExchangeOffered &&
    allExchangeClaims &&
    hasReturnLabel
  );
};

const getDefaultExchangeMethod = (
  offeredMethods: ReturnType<typeof getOfferedExchangeMethods>,
  exchangeMethodParam?: ExchangeMethod | null,
) => {
  const exchangeMethod = offeredMethods.find(
    (method) => method.type === exchangeMethodParam,
  );
  if (exchangeMethod) {
    return exchangeMethod;
  }

  const instantExchangeMethod = offeredMethods.find(
    (method) => method.type === ExchangeMethod.instant,
  );
  if (instantExchangeMethod) {
    return instantExchangeMethod;
  }

  return (
    offeredMethods.find((method) => method.type === ExchangeMethod.standard) ??
    null
  );
};

type ExchangeOption = {
  type: ExchangeMethod;
  label: string;
  description: string;
};
const getOfferedExchangeMethods = (
  isInstantExchangeAvailable: boolean,
  isInstantExchangeRequired: boolean,
): ExchangeOption[] => {
  const requireInstantExchange =
    isInstantExchangeRequired && isInstantExchangeAvailable;

  const exchangeOptions: ExchangeOption[] = [];

  if (isInstantExchangeAvailable) {
    exchangeOptions.push({
      type: ExchangeMethod.instant,
      label: exchangeMethodLabel[ExchangeMethod.instant],
      description: `Use a credit card to get your items sent immediately.`,
    });
  }

  if (!requireInstantExchange) {
    exchangeOptions.push({
      type: ExchangeMethod.standard,
      label: exchangeMethodLabel[ExchangeMethod.standard],
      description: 'Send your items back first, and get your new items later.',
    });
  }

  return exchangeOptions;
};

export function getExchangeMethods(
  context: CrewContext,
  exchangeMethodParam?: ExchangeMethod | null,
) {
  const isInstantExchangeRequired =
    !!context.order?.offerInstantExchange?.isInstantExchangeRequired;

  const offered = getOfferedExchangeMethods(
    checkInstantExchangeAvailable(context),
    isInstantExchangeRequired,
  );
  return {
    offered,
    selected: getDefaultExchangeMethod(offered, exchangeMethodParam),
  };
}
